import { useEffect, useRef, useState } from "react";
import { Play, Square, Settings, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import ComponentCard from "../../../../../shared/ui/common/ComponentCard";
import Button from "../../../../../shared/ui/button/Button";

import { AudioWsClient } from "../../../../../services/audio-ws-client";
import { useVoiceActivityDetection } from "../../../../../hooks/useVoiceActivityDetection";
import { isAiConsentRequired, uploadSessionAudio } from "../../../application/uploadSessionAudio";
import { LoadingOverlay } from "../../../../../shared/ui/common/LoadingOverlay";
import LiveSessionSettingsModal from "./LiveSessionSettingsModal";
import { useLiveInsightsContext } from "../../../../../context/LiveInsightsContextShared";
import { useBreakpoint } from "../../../../../hooks/useBreakpoint";
import { LAYOUT } from "../../../../../shared/layout";
import { useRecordingActivity } from "../../../../auth";
import {
  MIN_SESSION_AUDIO_MINUTES,
  MIN_SESSION_AUDIO_SECONDS,
  formatDuration,
} from "../../../domain/audioMinimum";

interface LiveSessionProps {
  /** Where "finish later" goes: the sessions of the record this one belongs to. */
  exitPath: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sessionId: string | any;
  onBack: () => void;
  onContinue: () => void;
}

export default function SessionAddLiveForm({
  sessionId,
  exitPath,
  onBack,
  onContinue,
}: LiveSessionProps) {
  const { t } = useTranslation();
  const streamRef = useRef<MediaStream | null>(null);
  const wsClientRef = useRef<AudioWsClient | null>(null);
  const timerRef = useRef<number | null>(null); // ✅ navegador
  const { addMessage, setConnectionState } = useLiveInsightsContext();

  const [devices, setDevices] = useState<{ label: string; value: string }[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string>("");

  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderMinutes, setReminderMinutes] = useState(60);
  const reminderEnabledRef = useRef(reminderEnabled);
  const reminderMinutesRef = useRef(reminderMinutes);
  useEffect(() => {
    reminderEnabledRef.current = reminderEnabled;
    reminderMinutesRef.current = reminderMinutes;
  }, [reminderEnabled, reminderMinutes]);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isOverTime, setIsOverTime] = useState(false);

  const [elapsedTime, setElapsedTime] = useState(0); // ⏱️ tiempo acumulado en segundos

  const [isProcessing, setIsProcessing] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();

  const { isSpeaking, level } = useVoiceActivityDetection(streamRef.current);
  const { setRecordingActive } = useRecordingActivity();

  // Suspend the auth inactivity timeout while the mic is recording so a
  // silent-but-active session does not get logged out and discard the
  // in-memory audio blob.
  useEffect(() => {
    if (!recording) return;
    setRecordingActive(true);
    return () => setRecordingActive(false);
  }, [recording, setRecordingActive]);

  useEffect(() => {
    // Get available audio devices
    const getDevices = async () => {
      try {
        const devs = await navigator.mediaDevices.enumerateDevices();
        const audioDevs = devs
          .filter((d) => d.kind === "audioinput")
          .map((d) => ({
            label: d.label || `Microphone ${d.deviceId.substring(0, 4)}...`,
            value: d.deviceId,
          }));
        setDevices(audioDevs);
        if (audioDevs.length > 0 && !selectedDevice) {
          setSelectedDevice(audioDevs[0].value);
        }
      } catch (e) {
        console.error("Error fetching devices", e);
      }
    };
    getDevices();

    return () => {
      // 🧹 Cleanup: stop recording if component unmounts
      if (wsClientRef.current) {
        wsClientRef.current.stop();
        wsClientRef.current = null;
      }
      stopTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRecording = async () => {
    if (recording) return;

    setError(null);

    // Reuse the paused client to keep a single continuous webm recording
    if (wsClientRef.current?.isPaused()) {
      wsClientRef.current.resume();
      setRecording(true);
      startTimer();
      return;
    }

    const id = typeof sessionId === "string" ? sessionId : sessionId.id;
    const wsUrl = `/api/sessions/ws/live/${id}`;

    wsClientRef.current = new AudioWsClient({
      wsUrl,

      onPartialTranscript: () => {
        // No-op - STT is disabled for now
      },

      onFinalTranscript: () => {
        // No-op - STT is disabled for now
      },

      onError: (err) => {
        setError(err);
        stopRecording();
      },

      onLiveInsight: (msg) => {
        if (addMessage) {
          addMessage(msg);
        }
      },

      onOpen: () => {
        setRecording(true);
        setConnectionState('connected');
        startTimer(); // ⏱️ start timer
      },

      onClose: () => {
        setRecording(false);
        setConnectionState('disconnected');
        stopTimer(); // ⏱️ stop timer
      },
    });

    try {
      await wsClientRef.current.start(true, selectedDevice);
      streamRef.current = wsClientRef.current.getStream();
    } catch (err) {
      console.error("❌ Error starting audio streaming:", err);
      setError(
        err instanceof Error
          ? err.message
          : t("session.live.errorStream")
      );
      stopRecording();
    }
  };

  const stopRecording = () => {
    wsClientRef.current?.pause();
    setRecording(false);
    stopTimer(); // ⏱️ stop timer
  };

  // ⏱️ Temporizador acumulativo
  const startTimer = () => {
    if (timerRef.current !== null) return; // evitar multiples timers
    timerRef.current = window.setInterval(() => {
      setElapsedTime((prev) => {
        const newVal = prev + 1;
        // Check reminder
        // Read through refs: this interval outlives the render that created it.
        if (reminderEnabledRef.current && newVal >= reminderMinutesRef.current * 60) {
          setIsOverTime(true);
        }
        return newVal;
      });
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };



  const MIN_RECORDING_SECONDS = MIN_SESSION_AUDIO_SECONDS;

  const handleContinue = async () => {
    // 1. Fully stop the recording (whether currently recording or paused) to flush all data
    const wasRecording = recording;
    if (recording) stopRecording();

    if (elapsedTime < MIN_RECORDING_SECONDS) {
      setError(
        t("session.live.errorTooShort", {
          minutes: MIN_SESSION_AUDIO_MINUTES,
        })
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const id = typeof sessionId === "string" ? sessionId : sessionId.id;

      // 2. Do a full stop to release the mic and flush any remaining MediaRecorder data
      const client = wsClientRef.current;
      wsClientRef.current = null;
      await client?.stop();
      const audioBlob = client?.getRecordedAudio() ?? null;

      if (audioBlob && audioBlob.size > 0) {
        const contentType = audioBlob.type || "audio/webm";

        // 3. Upload and trigger processing. The recorder's clock is the
        // session's real length; it replaces the planned one.
        await uploadSessionAudio(id, audioBlob, contentType, elapsedTime);

        setIsProcessing(true);
      } else {
        // Fallback or error if no audio was captured
        if (wasRecording) {
          setError(t("session.live.errorNoAudio", "No audio was captured during the session."));
        } else {
          onContinue();
        }
      }
    } catch (err) {
      console.error("Error during live session finalization:", err);
      if (isAiConsentRequired(err)) {
        setError(t("patient.portalConsent.aiBlocked"));
      } else {
        setError(t("session.live.errorGen"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFinishLater = () => {
    navigate(exitPath);
  };

  if (isProcessing) {
    return (
      <ComponentCard title={t("session.audio.processingTitle", "Audio is being processed")}>
        <div className="flex flex-col items-center justify-center p-8 space-y-6 text-center">
          <div className="w-16 h-16 bg-accent-tint rounded-full flex items-center justify-center mb-2">
            <CheckCircle2 className="w-8 h-8 text-accent-text" />
          </div>

          <h3 className="text-xl font-bold text-gray-900">
            {t("session.audio.processingHeader", "Your audio is processing in the background")}
          </h3>

          <p className="text-gray-600 max-w-md">
            {t(
              "session.audio.processingDescription",
              "We've safely queued your audio. Scribe will transcribe the audio, generate clinical notes, and run the AI analysis. We'll notify you as soon as it's ready!"
            )}
          </p>

          <div className="pt-6 w-full max-w-xs space-y-3">
            <Button
              className="w-full"
              onClick={handleFinishLater}
            >
              {t("session.audio.goToDashboard", "Go to Dashboard")}
            </Button>
          </div>
        </div>
      </ComponentCard>
    );
  }

  return (
    <div className={`flex w-full ${isMobile ? 'flex-col' : 'gap-6'}`} style={{ paddingBottom: isMobile ? LAYOUT.SAFE_AREA_PADDING : 0 }}>
      <div className={`flex-1 rounded-2xl border ${isMobile ? 'p-6' : 'p-8'} flex flex-col space-y-8 bg-white`}>

        {/* RECORDING */}
          <div className="flex flex-col items-center space-y-4">
            <h3 className="font-semibold text-lg">{t("session.live.title")}</h3>

            <div className={`relative flex items-center justify-center ${isMobile ? 'w-36 h-36' : 'w-44 h-44'}`}>
              {/* Outer pulsing ring when recording */}
              {recording && (
                <div 
                  className="absolute inset-0 rounded-full bg-purple-100 animate-pulse opacity-40" 
                  style={{ 
                    transform: `scale(${1 + level * 0.2})`,
                  }} 
                />
              )}
              
              <div
                className={`
                  relative w-32 h-32 rounded-full flex items-center justify-center transition-all duration-300
                  ${recording ? "bg-purple-50 shadow-inner" : "bg-slate-100"}
                `}
                style={{
                  boxShadow: recording && isSpeaking 
                    ? `0 0 ${20 + level * 30}px rgba(138, 73, 214, 0.3)` 
                    : "none"
                }}
              >
                <div 
                  className="transition-colors duration-200"
                  style={{ color: recording && isSpeaking ? "#8A49D6" : recording ? "#64748b" : "#94a3b8" }}
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="64" 
                    height="64" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    className="transition-transform duration-200"
                    style={{ transform: `scale(${1 + level * 0.1})` }}
                  >
                    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" x2="12" y1="19" y2="22"/>
                  </svg>
                </div>
              </div>
            </div>


          {/* ⏱️ Temporizador */}
          <div className={`flex flex-col items-center transition-colors duration-300 ${isOverTime ? "text-red-500 animate-pulse" : "text-gray-600"}`}>
            <p className="text-sm font-mono text-xl font-bold tracking-wider">{formatTime(elapsedTime)}</p>
            {isOverTime && (
              <span className="text-xs font-semibold flex items-center gap-1 mt-1">
                <p>🔔 {t("session.live.timeLimitReached", "Time Limit Reached")}</p>
              </span>
            )}
          </div>

          {/* CONTROLS */}
          <div className="flex flex-col items-center gap-6 pt-2">
            {!recording ? (
              <div className="flex flex-col items-center gap-2">
                <button
                  onClick={startRecording}
                  className="flex items-center gap-2 rounded-full bg-accent px-8 py-4 font-semibold text-accent-contrast transition-colors duration-[--duration-fast] hover:bg-accent-hover active:scale-[0.98]"
                  style={{ minHeight: LAYOUT.TOUCH_TARGET_SIZE, minWidth: '200px' }}
                >
                  <Play className="w-6 h-6 fill-current" />
                  {t("session.live.startRecording", "Start Recording")}
                </button>
                {/* Said before the therapist starts, not after they stop with
                    too little to work with. */}
                <p className="max-w-md text-center text-sm text-content-secondary">
                  {t("session.minAudio.notice", {
                    minutes: MIN_SESSION_AUDIO_MINUTES,
                  })}
                </p>
                {elapsedTime > 0 && elapsedTime < MIN_RECORDING_SECONDS && (
                  <p className="text-sm text-content-secondary">
                    {t("session.live.minDurationHint", {
                      remaining: formatDuration(MIN_RECORDING_SECONDS - elapsedTime),
                      minutes: MIN_SESSION_AUDIO_MINUTES,
                    })}
                  </p>
                )}
              </div>
            ) : (
              <button
                onClick={stopRecording}
                className="flex items-center gap-2 rounded-full bg-status-critical-fill px-8 py-4 font-semibold text-content-inverse transition-colors duration-[--duration-fast] hover:bg-status-critical-fill/90 active:scale-[0.98]"
                style={{ minHeight: LAYOUT.TOUCH_TARGET_SIZE, minWidth: '200px' }}
              >
                <span aria-hidden="true" className="relative flex size-2.5">
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-content-inverse opacity-60" />
                  <span className="relative inline-flex size-2.5 rounded-full bg-content-inverse" />
                </span>
                <Square className="size-5 fill-current" />
                {t("session.live.stopRecording", "Stop Recording")}
              </button>
            )}

            {/* Manage Settings Link */}
            <button
              onClick={() => setIsSettingsOpen(true)}
              disabled={recording}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-accent-text transition-colors disabled:opacity-50 disabled:cursor-not-allowed p-2"
              style={{ minHeight: LAYOUT.TOUCH_TARGET_SIZE }}
            >
              <Settings className="w-5 h-5" />
              {t("session.live.manageSettings", "Manage recording settings")}
            </button>
          </div>

        </div>

        {error && <p className="text-red-500 text-center">{error}</p>}


        {/* ACTIONS */}
        <div className={`flex justify-center gap-4 pt-6 mt-auto border-t ${isMobile ? 'flex-col' : ''}`}>
          <button
            onClick={onBack}
            className={`px-4 py-2 rounded-xl border text-sm font-semibold transition-colors hover:bg-gray-50 
              ${isMobile ? 'order-2 w-full text-gray-500' : ''}`}
            style={isMobile ? { minHeight: LAYOUT.TOUCH_TARGET_SIZE } : {}}
          >
            {t("session.back")}
          </button>

          <button
            onClick={handleContinue}
            disabled={recording || elapsedTime < MIN_RECORDING_SECONDS || loading}
            className={`px-6 py-2 rounded-xl bg-accent text-white text-sm font-semibold shadow-md transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed
              ${isMobile ? 'order-1 w-full py-3 text-base shadow-raised' : ''}`}
            style={isMobile ? { minHeight: LAYOUT.TOUCH_TARGET_SIZE } : {}}
          >
            {t("session.continue")}
          </button>
        </div>

        {/* LOADING OVERLAY */}
        <LoadingOverlay
          visible={loading}
          message={t("session.live.processing")}
        />

        {/* SETTINGS MODAL */}
        <LiveSessionSettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          devices={devices}
          selectedDevice={selectedDevice}
          onSelectDevice={setSelectedDevice}
          reminderEnabled={reminderEnabled}
          onToggleReminder={setReminderEnabled}
          reminderMinutes={reminderMinutes}
          onChangeReminderMinutes={setReminderMinutes}
          disabled={recording}
        />
      </div>
    </div>
  );
}
